import React, { useState, useEffect, ChangeEvent } from 'react';
import {
    generateMapLibreStyle,
    type GenerateMapLibreStyleOptions,
    type AppLayerDefinition,
    type AppLayerSourceDefinition,
    type AppLayerStyleDefinition,
} from '../../utils/maplibreStyleExportUtils';
import { downloadFile } from '../../utils/exportUtils';
import useGeoData from '../../../../store/useGeoData';
import { selectAllAppLayers } from '../../../../store/layerSlice';
import { selectAllLayerStyles } from '../../../../store/layerStyleSlice';
import { selectMapView } from '../../../../store/mapViewSlice';
import type { LayerStyleProperties } from '../../../../store/types';
import { useShallow } from 'zustand/react/shallow';

// import { TextInput } from '../../../../common/inputs/TextInput/TextInput'; // Non più necessario qui
import { ExtendedTextInput } from '../../../../common/inputs/TextInput/ExtendedTextInput';
import { NumberInput } from '../../../../common/inputs/NumberInput/NumberInput';
import UrlInput from '../../../../common/inputs/UrlInput/UrlInput';
import ToggleButton from '../../../../common/inputs/ToggleButton/ToggleButton';
import { Copy } from 'lucide-react';

interface MapViewProps {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
}

interface AppLayerInfo {
  id: string;
  name: string;
}

interface ExportMapLibreStyleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentMapView: MapViewProps;
  availableLayers: AppLayerInfo[];
  applicationLayers: AppLayerDefinition[];
  applicationSources: { [sourceId: string]: AppLayerSourceDefinition };
  applicationLayerStyles: { [layerId: string]: AppLayerStyleDefinition };
}

const ExportMapLibreStyleDialog: React.FC<ExportMapLibreStyleDialogProps> = ({
  isOpen,
  onClose,
  currentMapView,
  availableLayers = [],
  applicationLayers,
  applicationSources,
  applicationLayerStyles,
}) => {
  const [glyphsUrl, setGlyphsUrl] = useState("https://raw.githubusercontent.com/latidudemaps/GeologiaVDA/main/data/fonts/{fontstack}/{range}.pbf");
  const [spriteUrl, setSpriteUrl] = useState("https://demotiles.maplibre.org/styles/osm-bright-gl-style/sprite");
  const [styleName, setStyleName] = useState("My MapLibre Style");
  const [basemapTileUrl, setBasemapTileUrl] = useState("");
  const [basemapSourceId, setBasemapSourceId] = useState("basemap-source");

  const [selectedLayerIds, setSelectedLayerIds] = useState<Set<string>>(new Set());

  const [useCurrentMapViewValues, setUseCurrentMapViewValues] = useState(true);
  const [manualCenterLon, setManualCenterLon] = useState<number | string>(currentMapView.center[0]);
  const [manualCenterLat, setManualCenterLat] = useState<number | string>(currentMapView.center[1]);
  const [manualZoom, setManualZoom] = useState<number | string>(currentMapView.zoom);
  const [manualBearing, setManualBearing] = useState<number | string>(currentMapView.bearing);
  const [manualPitch, setManualPitch] = useState<number | string>(currentMapView.pitch);

  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [jsonPreviewContent, setJsonPreviewContent] = useState("");
  const [copyButtonText, setCopyButtonText] = useState("Copia"); // Questo stato verrà modificato per mostrare l'icona o "Copiato!"

  const handleCopyJson = () => {
    if (jsonPreviewContent) {
      navigator.clipboard.writeText(jsonPreviewContent)
        .then(() => {
          setCopyButtonText("Copiato!");
          setTimeout(() => setCopyButtonText("Copia"), 2000);
        })
        .catch(err => {
          console.error("Errore durante la copia del JSON:", err);
          setCopyButtonText("Errore");
          setTimeout(() => setCopyButtonText("Copia"), 2000);
        });
    }
  };

  useEffect(() => {
    if (useCurrentMapViewValues && currentMapView) {
      setManualCenterLon(currentMapView.center[0]);
      setManualCenterLat(currentMapView.center[1]);
      setManualZoom(currentMapView.zoom);
      setManualBearing(currentMapView.bearing);
      setManualPitch(currentMapView.pitch);
    }
  }, [
    useCurrentMapViewValues,
    currentMapView?.center[0],
    currentMapView?.center[1],
    currentMapView?.zoom,
    currentMapView?.bearing,
    currentMapView?.pitch,
  ]);

  useEffect(() => {
    if (availableLayers.length > 0) {
      setSelectedLayerIds(new Set(availableLayers.map(layer => layer.id)));
    } else {
      setSelectedLayerIds(new Set());
    }
  }, [availableLayers]);

  // Funzione helper per assicurare che minzoom, maxzoom e filter siano presenti
  const ensureLayerProperties = React.useCallback((
    styleObject: any,
    // applicationLayers è già nello scope del componente dalle props
  ): any => {
    if (!styleObject || !Array.isArray(styleObject.layers) || !applicationLayers) {
      return styleObject;
    }

    const appLayerDefMap = new Map(applicationLayers.map(l => [l.id, l]));

    const newLayers = styleObject.layers.map((layer: any) => {
      const appLayerDef = appLayerDefMap.get(layer.id);
      if (appLayerDef) {
        // Crea una copia del layer per evitare modifiche in-place non volute
        const newLayer = { ...layer };
        // minZoom e maxZoom non devono essere aggiunti qui,
        // sono gestiti a livello di sorgente.
        // if (appLayerDef.minZoom !== undefined) {
        //   newLayer.minzoom = appLayerDef.minZoom; // Chiave lowercase
        // }
        // if (appLayerDef.maxZoom !== undefined) {
        //   newLayer.maxzoom = appLayerDef.maxZoom; // Chiave lowercase
        // }
        if (appLayerDef.filter !== undefined) {
          newLayer.filter = appLayerDef.filter;
        }
        return newLayer;
      }
      return layer;
    });
    
    // Restituisce un nuovo oggetto stile con i layer modificati
    return { ...styleObject, layers: newLayers };
  }, [applicationLayers]);

  useEffect(() => {
    if (!isOpen || !showJsonPreview) {
      if (!showJsonPreview) setJsonPreviewContent("");
      return;
    }

    const mapViewToUse: MapViewProps = useCurrentMapViewValues
      ? currentMapView
      : {
          center: [Number(manualCenterLon), Number(manualCenterLat)],
          zoom: Number(manualZoom),
          bearing: Number(manualBearing),
          pitch: Number(manualPitch),
        };

    const finalSelectedLayerIds = Array.from(selectedLayerIds).filter(id =>
      applicationLayers.some(l => l.id === id) // applicationLayers è già filtrato dall'hook
    );

    const options: GenerateMapLibreStyleOptions = {
      glyphsUrl,
      spriteUrl: spriteUrl || undefined,
      styleName,
      mapView: mapViewToUse,
      selectedLayerIds: finalSelectedLayerIds,
    };

    if (basemapTileUrl && basemapSourceId) {
      options.basemap = {
        tileUrl: basemapTileUrl,
        sourceId: basemapSourceId,
      };
    }

    try {
      const rawStyleObject = generateMapLibreStyle(
        applicationLayers,
        applicationSources,
        applicationLayerStyles,
        options
      );
      const finalStyleObject = ensureLayerProperties(rawStyleObject); // Applica la funzione helper
      setJsonPreviewContent(JSON.stringify(finalStyleObject, null, 2));
    } catch (error) {
      console.error("Errore durante la generazione dell'anteprima JSON:", error);
      setJsonPreviewContent("Errore nella generazione dell'anteprima.");
    }
  }, [
    isOpen, showJsonPreview, glyphsUrl, spriteUrl, styleName,
    basemapTileUrl, basemapSourceId, useCurrentMapViewValues,
    manualCenterLon, manualCenterLat, manualZoom, manualBearing, manualPitch,
    selectedLayerIds, applicationLayers, applicationSources, applicationLayerStyles,
    currentMapView, ensureLayerProperties // ensureLayerProperties è ora definita prima
  ]);

  if (!isOpen) {
    return null;
  }

  const handleExport = () => {
    const mapViewToUse: MapViewProps = useCurrentMapViewValues
      ? currentMapView
      : {
          center: [Number(manualCenterLon), Number(manualCenterLat)],
          zoom: Number(manualZoom),
          bearing: Number(manualBearing),
          pitch: Number(manualPitch),
        };

    const finalSelectedLayerIds = Array.from(selectedLayerIds).filter(id =>
      applicationLayers.some(l => l.id === id) // applicationLayers è già filtrato dall'hook
    );

    const options: GenerateMapLibreStyleOptions = {
      glyphsUrl,
      spriteUrl: spriteUrl || undefined,
      styleName,
      mapView: mapViewToUse,
      selectedLayerIds: finalSelectedLayerIds,
    };

    if (basemapTileUrl && basemapSourceId) {
      options.basemap = {
        tileUrl: basemapTileUrl,
        sourceId: basemapSourceId,
      };
    }

    try {
      const rawStyleObject = generateMapLibreStyle(
        applicationLayers,
        applicationSources,
        applicationLayerStyles,
        options
      );
      const finalStyleObject = ensureLayerProperties(rawStyleObject);
      const styleString = JSON.stringify(finalStyleObject, null, 2);
      const blob = new Blob([styleString], { type: 'application/json;charset=utf-8' });
      downloadFile(blob, `${styleName.replace(/\s+/g, '_') || 'maplibre-style'}.json`);
      onClose();
    } catch (error) {
      console.error("Errore durante l'esportazione dello stile MapLibre:", error);
      alert(`Errore durante l'esportazione: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleLayerSelectionChange = (layerId: string) => {
    setSelectedLayerIds(prevSelectedIds => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (newSelectedIds.has(layerId)) {
        newSelectedIds.delete(layerId);
      } else {
        newSelectedIds.add(layerId);
      }
      return newSelectedIds;
    });
  };

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (event: ChangeEvent<HTMLInputElement> | { target: { value: string } } | string) => {
    if (typeof event === 'string') {
      setter(event);
    } else {
      setter(event.target.value);
    }
  };
  
  const handleNumberInputChange = (setter: React.Dispatch<React.SetStateAction<number | string>>) => (value: number | string | undefined) => {
    setter(value ?? '');
  };


  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 'var(--z-index-dialog)',
    }}>
      <div style={{
        backgroundColor: 'var(--color-background-ui)',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        minWidth: showJsonPreview ? '800px' : '300px', // Aumentato minWidth quando JSON è visibile
        maxWidth: showJsonPreview ? '1000px' : '500px', // Aumentato maxWidth quando JSON è visibile
        width: showJsonPreview ? '80vw' : 'auto', // Aumentato width a 80vw quando JSON è visibile
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        transition: 'min-width 0.3s ease-in-out, max-width 0.3s ease-in-out, width 0.3s ease-in-out',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px', flexShrink: 0, position: 'relative' }}>
          <h2 style={{ margin: 0, textAlign: 'center', flexGrow: 1, fontSize: 'var(--font-size-xlarge, 1.5rem)', color: 'var(--color-text-primary)' }}>Export MapLibre Style</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              position: 'absolute', // Posiziona il pulsante di chiusura
              right: '0px',      // All'estrema destra
              top: '50%',        // Centrato verticalmente
              transform: 'translateY(-50%)' // Correzione per centratura verticale precisa
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ display: 'flex', flexGrow: 1, overflow: 'hidden', gap: '16px' }}>
          <div style={{
              flex: showJsonPreview ? '1 1 40%' : '1 1 100%', // Ridotta la porzione per i form
              minWidth: showJsonPreview ? '320px' : 'auto', // Leggermente ridotto per dare più spazio al JSON
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              overflowY: 'auto',
              maxHeight: 'calc(90vh - 160px)',
              paddingRight: '10px',
          }}>
            <div>
              <label htmlFor="glyphsUrl" className="input-label">URL Glyphs:</label>
              <UrlInput
                id="glyphsUrl"
                value={glyphsUrl}
                onChange={handleInputChange(setGlyphsUrl)}
                placeholder="Inserisci URL glyphs"
              />
            </div>
            <div>
              <label htmlFor="spriteUrl" className="input-label">URL Sprite:</label>
              <UrlInput
                id="spriteUrl"
                value={spriteUrl}
                onChange={handleInputChange(setSpriteUrl)}
                placeholder="Inserisci URL sprite (opzionale)"
              />
            </div>
            <div>
              <label htmlFor="styleName" className="input-label">Style Name:</label>
              <ExtendedTextInput
                id="styleName" // id è ora richiesto da ExtendedTextInput
                name="styleName" // Passiamo anche il name
                value={styleName}
                onChange={handleInputChange(setStyleName)}
                placeholder="my style"
                // La label esterna è già presente, quindi non passiamo la prop label a ExtendedTextInput
              />
            </div>

            {/* Sezione Selezione Layer */}
            <div style={{ paddingTop: '16px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Choose layers to keep</h3>
              <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--color-border-ui-secondary)', borderRadius: 'var(--border-radius-medium)', padding: '8px' }}>
                {availableLayers.length > 0 ? (
                  availableLayers.map(layer => (
                    <div key={layer.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', gap: 'var(--spacing-xs, 8px)', justifyContent: 'flex-start' }}>
                      <ToggleButton
                        id={`export-layer-toggle-${layer.id}`}
                        isOn={selectedLayerIds.has(layer.id)}
                        onToggle={() => handleLayerSelectionChange(layer.id)}
                      />
                      <label htmlFor={`export-layer-toggle-${layer.id}`} className="input-label" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {layer.name}
                      </label>
                    </div>
                  ))
                ) : (
                  <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>No layer available</p>
                )}
              </div>
            </div>

            <div style={{ paddingTop: '16px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Basemap Config</h3>
              <div>
                <label htmlFor="basemapTileUrl" className="input-label">Basemap tiles URL (XYZ or TileJSON):</label>
                <UrlInput
                  id="basemapTileUrl"
                  value={basemapTileUrl}
                  onChange={handleInputChange(setBasemapTileUrl)}
                  placeholder="es. https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </div>
              <div style={{ marginTop: '10px' }}>
                <label htmlFor="basemapSourceId" className="input-label">Basemap source ID:</label>
                <ExtendedTextInput
                  id="basemapSourceId" // id è ora richiesto
                  name="basemapSourceId" // Passiamo anche il name
                  value={basemapSourceId}
                  onChange={handleInputChange(setBasemapSourceId)}
                  placeholder="es. openstreetmap-source"
                  // La label esterna è già presente
                />
              </div>
            </div>

            <div style={{ paddingTop: '16px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '12px' }}>MapView parameters</h3>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', gap: 'var(--spacing-xs, 8px)', justifyContent: 'flex-start' }}>
                  <ToggleButton
                    id="use-current-map-view-toggle"
                    isOn={useCurrentMapViewValues}
                    onToggle={() => setUseCurrentMapViewValues(!useCurrentMapViewValues)}
                  />
                  <label htmlFor="use-current-map-view-toggle" className="input-label" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Use current map view values
                  </label>
                </div>
              </div>
              {/* Prima riga: Longitudine e Latitudine */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="manualCenterLon" className="input-label">Center Longitude:</label>
                  <NumberInput
                    id="manualCenterLon"
                    value={Number(manualCenterLon)}
                    onChange={handleNumberInputChange(setManualCenterLon)}
                    disabled={useCurrentMapViewValues}
                    step={0.001}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="manualCenterLat" className="input-label">Center Latitude:</label>
                  <NumberInput
                    id="manualCenterLat"
                    value={Number(manualCenterLat)}
                    onChange={handleNumberInputChange(setManualCenterLat)}
                    disabled={useCurrentMapViewValues}
                    step={0.001}
                  />
                </div>
              </div>

              {/* Seconda riga: Zoom, Bearing, Pitch */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="manualZoom" className="input-label">Zoom:</label>
                  <NumberInput
                    id="manualZoom"
                    value={Number(manualZoom)}
                    onChange={handleNumberInputChange(setManualZoom)}
                    disabled={useCurrentMapViewValues}
                    step={0.1}
                    min={0}
                    max={22}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="manualBearing" className="input-label">Bearing:</label>
                  <NumberInput
                    id="manualBearing"
                    value={Number(manualBearing)}
                    onChange={handleNumberInputChange(setManualBearing)}
                    disabled={useCurrentMapViewValues}
                    step={1}
                    min={0}
                    max={360}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="manualPitch" className="input-label">Pitch:</label>
                  <NumberInput
                    id="manualPitch"
                    value={Number(manualPitch)}
                    onChange={handleNumberInputChange(setManualPitch)}
                    disabled={useCurrentMapViewValues}
                    step={1}
                    min={0}
                    max={85}
                  />
                </div>
              </div>
            </div>
          </div>

          {showJsonPreview && (
            <div style={{
              flex: '1 1 60%', // Aumentata la porzione per l'anteprima JSON
              minWidth: '450px', // Aumentato il minWidth per l'anteprima JSON
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '1px solid var(--color-border-ui-secondary, #555)',
              paddingLeft: '16px',
              overflow: 'hidden',
              maxHeight: 'calc(90vh - 160px)',
              position: 'relative',
            }}>
              <button
                onClick={handleCopyJson}
                title={copyButtonText === "Copia" ? "Copia JSON" : copyButtonText}
                style={{
                  position: 'absolute',
                  bottom: '20px', // Spostato in basso
                  right: '10px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  zIndex: 1,
                  backgroundColor: 'var(--color-button-icon, var(--color-button-secondary))', // Colore di sfondo per pulsanti icona
                  color: 'var(--color-text-button)',
                  border: '1px solid var(--color-border-button-secondary)',
                  borderRadius: 'var(--border-radius-medium)', // Raggio del bordo più grande
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s ease, transform 0.1s ease', // Transizioni per feedback
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)', // Ombra leggera
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'} // Effetto pressione
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'} // Reset su mouse leave
              >
                {copyButtonText === "Copia" ? <Copy size={16} /> : copyButtonText}
              </button>
              <pre style={{
                backgroundColor: 'var(--color-background-input, #333)',
                color: 'var(--color-text-panel, #eee)',
                border: '1px solid var(--color-border-ui-secondary, #555)',
                padding: '12px',
                borderRadius: 'var(--border-radius-small, 4px)',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                fontFamily: 'monospace, "Courier New", Courier',
                fontSize: 'var(--font-size-small, 12px)',
                lineHeight: '1.4',
                flexGrow: 1,
              }}>
                {jsonPreviewContent}
              </pre>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '20px', flexShrink: 0, paddingTop: '16px', borderTop: '1px solid var(--color-border-subtle)' }}>
          <button
            onClick={() => setShowJsonPreview(!showJsonPreview)}
            style={{ padding: '8px 16px', cursor: 'pointer', backgroundColor: 'var(--color-button-secondary)', color: 'var(--color-text-button)', border: '1px solid var(--color-border-button-secondary)', borderRadius: 'var(--border-radius-medium)' }}
          >
            {showJsonPreview ? "Hide JSON Preview" : "Show JSON Preview"}
          </button>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{ padding: '8px 16px', cursor: 'pointer' }}>
              Back
            </button>
            <button onClick={handleExport} style={{ padding: '8px 16px', cursor: 'pointer', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}>
              Download style.json
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const useMapLibreExportData = () => {
  const rawLayers = useGeoData(useShallow(selectAllAppLayers)) || [];
  const rawLayerStyles = useGeoData(useShallow(selectAllLayerStyles));
  const rawMapView = useGeoData(useShallow(selectMapView));

  const applicationLayers: AppLayerDefinition[] = [];
  const applicationSources: { [sourceId: string]: AppLayerSourceDefinition } = {};
  const applicationLayerStyles: { [layerId: string]: AppLayerStyleDefinition } = {};
  const validSourceTypes = ['vector', 'raster', 'raster-dem', 'geojson', 'image', 'video'];

  for (const appLayer of rawLayers) {

    if (!appLayer.id) {
      continue;
    }
    if (appLayer.maplibreLayerType === 'wms') {
      continue;
    }

    let appSourceDef: AppLayerSourceDefinition | null = null;
    const sourceIdForAppLayer = `${appLayer.id}_source_dialog`;

    if (appLayer.sourceSpecification) {
      const originalSourceSpec = appLayer.sourceSpecification; // Keep a reference to the original
      const exportedSource: AppLayerSourceDefinition = { ...originalSourceSpec } as AppLayerSourceDefinition;
      // Log the initial state of exportedSource.data for clarity

      // Ensure 'data' for geojson sources from URL is the URL string
      if (originalSourceSpec.type === 'geojson' &&
          typeof originalSourceSpec.data === 'string' &&
          (originalSourceSpec.data.startsWith('http://') || originalSourceSpec.data.startsWith('https://'))
      ) {
        // If originalSourceSpec.data is a URL string, ensure exportedSource.data is exactly that string.
        // The spread operator copy should have done this, but this is an explicit guarantee.
        exportedSource.data = originalSourceSpec.data;
      } else if (originalSourceSpec.type === 'geojson') {
        // For local GeoJSON files or other cases, 'data' might be an inline GeoJSON object or handled differently.
        // This subtask focuses on preserving the URL. For other GeoJSON 'data' types, current behavior (copy) is maintained.
      }

      // NUOVA LOGICA RICHIESTA DAL TASK:
      // Sovrascrivi 'data' se troviamo un URL originale su appLayer.url per sorgenti geojson
      if (exportedSource.type === 'geojson') {
        const originalUrl = (appLayer as any).url; // Ipotizziamo che 'appLayer.url' contenga l'URL originale
        if (typeof originalUrl === 'string' && (originalUrl.startsWith('http://') || originalUrl.startsWith('https://'))) {
            // Sovrascrivi solo se il dato attuale non è già questo URL, per evitare log ridondanti o sovrascritture non necessarie.
            if (exportedSource.data !== originalUrl) {
              exportedSource.data = originalUrl;
            } else {
            }
        } else {
            // Modificato il log per includere il valore di originalUrl se non è una stringa valida
        }
      }

      // The original console.log for exportedSource.type (original line 607) is now part of the more detailed log above,
      // or can be re-added if a separate log for just the type is desired after these modifications.

      if (!exportedSource.type) {
        // Se la sorgente viene saltata, anche il layer deve essere saltato.
        // Il continue qui salta la creazione della sorgente E del layer grazie alla ristrutturazione.
        continue;
      }

      // Assicura che minzoom/maxzoom siano lowercase, rimuovi proprietà non standard (name, crs) e pulisci undefined
      if (exportedSource.hasOwnProperty('minZoom') && typeof (exportedSource as any).minZoom !== 'undefined') {
        exportedSource.minzoom = (exportedSource as any).minZoom;
        delete (exportedSource as any).minZoom;
      }
      if (exportedSource.hasOwnProperty('maxZoom') && typeof (exportedSource as any).maxZoom !== 'undefined') {
        exportedSource.maxzoom = (exportedSource as any).maxZoom;
        delete (exportedSource as any).maxZoom;
      }

      // Rimuovi proprietà 'name' e 'crs' se esistono, poiché non sono standard per le sorgenti MapLibre
      // e potrebbero interferire o essere dati non necessari nello stile esportato.
      if (exportedSource.hasOwnProperty('name')) {
          delete (exportedSource as any).name;
      }
      if (exportedSource.hasOwnProperty('crs')) {
          delete (exportedSource as any).crs;
      }

      Object.keys(exportedSource).forEach(key => {
        if (exportedSource[key as keyof AppLayerSourceDefinition] === undefined) {
          delete exportedSource[key as keyof AppLayerSourceDefinition];
        }
      });
      
      // Ulteriore validazione del tipo di sorgente dopo la pulizia
      if (!exportedSource.type || !validSourceTypes.includes(exportedSource.type)) {
        // Se la sorgente viene saltata, anche il layer deve essere saltato.
        continue;
      }
      appSourceDef = exportedSource;

    } else {
      continue; // Salta l'intera iterazione per questo appLayer.
    }

    // Questo controllo if (!appSourceDef) dovrebbe essere ridondante con la logica sopra,
    // ma lo manteniamo per sicurezza o se la logica di creazione di appSourceDef diventasse più complessa.
    if (!appSourceDef) {
        console.error(`[Export Hook] CRITICAL: appSourceDef is null for layer ID: ${appLayer.id} even after processing. Skipping layer.`);
        continue;
    }
    
    // A questo punto, appSourceDef è valido e può essere aggiunto
    applicationSources[sourceIdForAppLayer] = appSourceDef;

    // SOLO SE la sorgente è stata creata con successo (siamo qui), crea e aggiungi il layerDef
    const appLayerDef: AppLayerDefinition = {
      id: appLayer.id,
      name: appLayer.name || appLayer.id,
      type: appLayer.maplibreLayerType,
      sourceId: sourceIdForAppLayer,
      sourceLayer: (appLayer as any).sourceLayer || undefined, // Ometti se undefined/vuoto
      minZoom: appLayer.minzoom,
      maxZoom: appLayer.maxzoom,
      filter: Array.isArray(appLayer.filter) ? appLayer.filter : undefined,
      isVisible: appLayer.visible !== false,
    };
    
    // Ometti chiavi opzionali se undefined
    if (appLayerDef.sourceLayer === undefined) delete appLayerDef.sourceLayer;
    if (appLayerDef.minZoom === undefined) delete appLayerDef.minZoom;
    if (appLayerDef.maxZoom === undefined) delete appLayerDef.maxZoom;
    if (appLayerDef.filter === undefined) delete appLayerDef.filter;

    applicationLayers.push(appLayerDef);

  } // fine del ciclo for


  const allPaintPropertyKeys = [
    'fill-antialias', 'fill-color', 'fill-opacity', 'fill-outline-color', 'fill-pattern', 'fill-sort-key', 'fill-translate', 'fill-translate-anchor',
    'line-blur', 'line-color', 'line-dasharray', 'line-gap-width', 'line-gradient', 'line-miter-limit', 'line-offset', 'line-opacity', 'line-pattern', 'line-round-limit', 'line-sort-key', 'line-translate', 'line-translate-anchor', 'line-width',
    'circle-blur', 'circle-color', 'circle-opacity', 'circle-pitch-alignment', 'circle-pitch-scale', 'circle-radius', 'circle-sort-key', 'circle-stroke-color', 'circle-stroke-opacity', 'circle-stroke-width', 'circle-translate', 'circle-translate-anchor',
    'heatmap-color', 'heatmap-intensity', 'heatmap-opacity', 'heatmap-radius', 'heatmap-weight',
    'fill-extrusion-base', 'fill-extrusion-color', 'fill-extrusion-height', 'fill-extrusion-opacity', 'fill-extrusion-pattern', 'fill-extrusion-translate', 'fill-extrusion-translate-anchor', 'fill-extrusion-vertical-gradient',
    'raster-brightness-max', 'raster-brightness-min', 'raster-contrast', 'raster-fade-duration', 'raster-hue-rotate', 'raster-opacity', 'raster-resampling', 'raster-saturation',
    'hillshade-accent-color', 'hillshade-exaggeration', 'hillshade-highlight-color', 'hillshade-illumination-anchor', 'hillshade-illumination-direction', 'hillshade-shadow-color',
    'icon-color', 'icon-halo-blur', 'icon-halo-color', 'icon-halo-width', 'icon-opacity', 'icon-translate', 'icon-translate-anchor',
    'text-color', 'text-halo-blur', 'text-halo-color', 'text-halo-width', 'text-opacity', 'text-translate', 'text-translate-anchor',
    'background-color', 'background-opacity', 'background-pattern'
  ];

  for (const layerId in rawLayerStyles) {
    // Assicurati che il layer esista in applicationLayers prima di processare il suo stile
    if (!applicationLayers.find(l => l.id === layerId)) {
        continue;
    }

    const styleProps = rawLayerStyles[layerId] as LayerStyleProperties;
    const paint: Record<string, any> = {};
    const layout: Record<string, any> = {};

    for (const key in styleProps) {
      if (Object.prototype.hasOwnProperty.call(styleProps, key)) {
        if (key.startsWith('_')) {
          continue;
        }
        const maplibreKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        const value = styleProps[key as keyof LayerStyleProperties];
        if (value === undefined || value === null) continue;

        if (allPaintPropertyKeys.includes(maplibreKey)) {
          paint[maplibreKey] = value;
        } else if (
          key !== 'isPopupEnabled' &&
          key !== 'popupFields' &&
          key !== 'availablePropertyKeys' &&
          key !== 'name' &&
          key !== 'id' &&
          key !== 'minZoom' &&
          key !== 'maxZoom' &&
          key !== 'filter'
        ) {
          layout[maplibreKey] = value;
        }
      }
    }
    
    const finalStyle: AppLayerStyleDefinition = {};
    if (Object.keys(paint).length > 0) {
      finalStyle.paint = paint;
    } else {
      finalStyle.paint = {};
    }

    if (Object.keys(layout).length > 0) {
      finalStyle.layout = layout;
    } else {
      finalStyle.layout = {};
    }
    applicationLayerStyles[layerId] = finalStyle;
  }
  
  const mapView: MapViewProps = {
    center: [rawMapView.longitude, rawMapView.latitude],
    zoom: rawMapView.zoom,
    bearing: rawMapView.bearing,
    pitch: rawMapView.pitch,
  };

  // La variabile finalApplicationLayers non è più necessaria qui perché applicationLayers è già corretta.
  return { applicationLayers, applicationSources, applicationLayerStyles, mapView };
};

export default ExportMapLibreStyleDialog;